import { closest } from '@jotforminc/utils';

import {
  addClass,
  forEach,
  hasClass,
  removeClass,
  replaceAll,
  parseQueryString
} from './utils';

export default class Progress {
  constructor(params) {
    this.selectIndex = this.selectIndex.bind(this);
    this.setValidIndexes = this.setValidIndexes.bind(this);
    this.setInvalidIndexes = this.setInvalidIndexes.bind(this);
    this.setIndexVisibility = this.setIndexVisibility.bind(this);
    this.setCurrentIndexText = this.setCurrentIndexText.bind(this);
    this.getVisibleIndex = this.getVisibleIndex.bind(this);
    this.bindSwipeOverviewMode = this.bindSwipeOverviewMode.bind(this);
    this.populateProgressItems = this.populateProgressItems.bind(this);
    this.hideProgressBar = this.hideProgressBar.bind(this);
    this.showProgressBar = this.showProgressBar.bind(this);
    this.setFirstLastCellClasses = this.setFirstLastCellClasses.bind(this);
    this.setQuestionOrderNumbers = this.setQuestionOrderNumbers.bind(this);
    this.shouldProgressDisplayed = this.shouldProgressDisplayed.bind(this);
    this.showProgressBarIfNeeded = this.showProgressBarIfNeeded.bind(this);
    this.init = this.init.bind(this);
    this.init(params);
  }

  init(params) {
    this.layoutElements = {
      progress: document.getElementById('cardProgress'),
      progressItems: null,
      progressToggle: document.getElementById('cardProgressToggle'),
      progressCurrentIndexText: document.getElementById('cardProgress-currentIndex'),
      progressQuestionCountText: document.getElementById('cardProgress-questionCount'),
      progressItemsLine: document.getElementsByClassName('jfProgress-itemWrapperLine')[0],
      progressItemWrapper: document.getElementsByClassName('jfProgress-itemWrapper')[0]
    };
    this.isFormProgressVisible = params.isFormProgressVisible;
    this.populateProgressItems(params.items);

    this.layoutElements.progressItems = this.layoutElements.progress.querySelectorAll('.jfProgress-item');
    this.layoutElements.progressItemCells = this.layoutElements.progress.querySelectorAll('.jfProgress-itemCell');
    this.cards = params.cards;

    this.layoutParams = {
      items: params.items,
      selectedIndex: params.selectedIndex || 0,
      hiddenIndexes: params.hiddenIndexes || [],
      validIndexes: params.validIndexes || [],
      invalidIndexes: params.invalidIndexes || [],
      deviceType: params.deviceType,
      isHidden: false,
      isFeedbackEmbedButton: params.isFeedbackEmbedButton,
      isSaveAndContinueLaterActivated: params.isSaveAndContinueLaterActivated
    };

    this.layoutParams.hiddenIndexes.forEach((isHidden, index) => {
      if (isHidden) {
        addClass(this.layoutElements.progressItemCells[index], 'isHidden');
      }
    });

    // eslint-disable-next-line no-multi-assign
    const events = this.events = {
      onFormOverviewClicked: params.onFormOverviewClicked,
      onItemSelected: params.onItemSelected
    };

    const { progressToggle, progressItems } = this.layoutElements;
    progressToggle.addEventListener('click', () => {
      events.onFormOverviewClicked();
    });
    this.bindSwipeOverviewMode();

    forEach(progressItems, progressItem => {
      (i => {
        progressItem.addEventListener('keydown', e => {
          if (e.keyCode === 32 || e.keyCode === 13) {
            e.target.click();
          }
        });
        progressItem.addEventListener('click', () => {
          events.onItemSelected(i);
        });
      })(+progressItem.getAttribute('data-index'));
    });

    this.setCurrentIndexText();
    this.setFirstLastCellClasses();
    this.setQuestionOrderNumbers();

    if (!this.shouldProgressDisplayed()) {
      this.hideProgressBar();
    }
  }

  bindSwipeOverviewMode() {
    let tStartY = null;
    let tEndY = null;
    const threshold = 70;
    document.addEventListener('touchstart', e => {
      if (closest(e.target, '#cardProgressToggle')) {
        tStartY = e.touches[0].screenY;
      } else {
        tStartY = null;
      }
    });

    document.addEventListener('touchmove', e => {
      tEndY = e.touches[0].screenY;
    });

    document.addEventListener('touchend', () => {
      if (tStartY !== null && tEndY !== null) {
        const isInScrollMode = hasClass(document.body, 'onScrollMode');
        let diff = tEndY - tStartY;
        diff = isInScrollMode ? diff : 0 - diff;
        if (diff >= threshold) {
          this.events.onFormOverviewClicked();
        }
      }
      tStartY = null;
      tEndY = null;
    });
  }

  selectIndex(index) {
    removeClass(this.layoutElements.progressItems[this.layoutParams.selectedIndex], 'isActive');
    this.layoutParams.selectedIndex = index;
    addClass(this.layoutElements.progressItems[this.layoutParams.selectedIndex], 'isActive');
    this.layoutElements.progressCurrentIndexText.innerText = `${this.getVisibleIndex(this.layoutParams.selectedIndex) + 1} `;
  }

  setValidIndexes(indexArr) {
    this.layoutParams.validIndexes = indexArr;
    forEach(this.layoutElements.progressItems, (p, index) => {
      if (indexArr.indexOf(index) > -1) {
        removeClass(p, 'hasError');
        addClass(p, 'isValid');
      } else {
        removeClass(p, 'isValid');
      }
    });
  }

  getVisibleIndex(index) {
    const invisibleCountToIndex = this.layoutParams.items.slice(0, index).reduce((acc, item, i) => {
      return this.layoutParams.hiddenIndexes[i] ? acc + 1 : acc;
    }, 0);

    return index - invisibleCountToIndex;
  }

  setCurrentIndexText() {
    const visibleItemCount = this.layoutParams.items.filter((p, index) => !this.layoutParams.hiddenIndexes[index]).length;
    this.layoutElements.progressQuestionCountText.innerText = ` ${visibleItemCount}`;
    this.layoutElements.progressCurrentIndexText.innerText = `${this.getVisibleIndex(this.layoutParams.selectedIndex) + 1} `;
  }

  setValidIndex(index) {
    const item = this.layoutElements.progressItems[index];
    removeClass(item, 'hasError');
    addClass(item, 'isValid');
  }

  setInvalidIndexes(indexArr) {
    this.layoutParams.invalidIndexes = indexArr;
    forEach(this.layoutElements.progressItems, (p, index) => {
      if (indexArr.indexOf(index) > -1) {
        removeClass(p, 'isValid');
        addClass(p, 'hasError');
      } else {
        removeClass(p, 'hasError');
      }
    });
  }

  setInvalidIndex(index) {
    const item = this.layoutElements.progressItems[index];
    removeClass(item, 'isValid');
    addClass(item, 'hasError');
  }

  setIndexVisibility(index, isVisible) {
    if (isVisible) {
      removeClass(this.layoutElements.progressItemCells[index], 'isHidden');
    } else {
      addClass(this.layoutElements.progressItemCells[index], 'isHidden');
    }

    this.layoutParams.hiddenIndexes[index] = !isVisible;
  }

  unsetInvalidIndex(index) {
    const item = this.layoutElements.progressItems[index];
    removeClass(item, 'hasError');
  }

  unsetValidIndex(index) {
    const item = this.layoutElements.progressItems[index];
    removeClass(item, 'isValid');
  }

  populateProgressItems(questions) {
    const cellTemplate = this.layoutElements.progress.querySelector('.jfProgress-itemCell');
    const newCells = questions.map((q, index) => {
      const cell = cellTemplate.cloneNode(true);
      cell.querySelector('.jfProgress-item').setAttribute('data-index', index);
      cell.querySelector('.jfProgress-item').setAttribute('aria-label', `jump to question ${index + 1}: ${q.label}`);
      cell.querySelector('.jfProgress-itemLabel').setAttribute('data-item-id', q.question ? q.question.id : '');
      cell.querySelector('.jfProgress-itemLabel').innerText = `${q.label}`;
      return cell;
    });
    const cellParent = cellTemplate.parentNode;
    cellParent.removeChild(cellTemplate);
    newCells.forEach(p => { cellParent.appendChild(p); });
  }

  hideProgressBar() {
    if (!this.layoutElements.isHidden) {
      addClass(this.layoutElements.progress, 'isHidden');
      this.layoutElements.progress.setAttribute('aria-hidden', 'true');
      this.layoutElements.isHidden = true;
    }
  }

  showProgressBar() {
    if (this.layoutElements.isHidden) {
      removeClass(this.layoutElements.progress, 'isHidden');
      this.layoutElements.progress.removeAttribute('aria-hidden');
      this.layoutElements.progress.removeAttribute('tabindex');
      this.layoutElements.isHidden = false;
    }
  }

  setFirstLastCellClasses() {
    let isFirstSet = false;
    let isLastSet = false;
    for (let i = 0; i < this.layoutElements.progressItemCells.length; i++) {
      const cell = this.layoutElements.progressItemCells[i];

      if (!isFirstSet) {
        if (!hasClass(cell, 'isHidden')) {
          isFirstSet = true;
          addClass(cell, 'isFirst');
        } else {
          removeClass(cell, 'isFirst');
        }
      } else {
        removeClass(cell, 'isFirst');
      }
    }

    for (let i = this.layoutElements.progressItemCells.length - 1; i >= 0; i--) {
      const cell = this.layoutElements.progressItemCells[i];

      if (!isLastSet) {
        if (!hasClass(cell, 'isHidden')) {
          isLastSet = true;
          addClass(cell, 'isLast');
        } else {
          removeClass(cell, 'isLast');
        }
      } else {
        removeClass(cell, 'isLast');
      }
    }
  }

  setQuestionOrderNumbers() {
    const visibleCells = [];
    forEach(this.layoutElements.progressItemCells, (cell, index) => {
      if (!hasClass(cell, 'isHidden')) {
        visibleCells.push({ cell, index });
      }
    });

    forEach(visibleCells, (c, index) => {
      const cell = this.layoutElements.progressItems[c.index];
      cell.querySelector('.jfProgress-itemLabel').innerText = `${index + 1}. ${this.cards[c.index].label}`;

      try {
        const queryValues = parseQueryString(global.location.search.substr(1));
        forEach(global.JotForm.calculations, calculation => {
          const { replaceText } = calculation;
          const sanitizer = value => {
            if (window.DomPurify) {
              return window.DomPurify.sanitize(value);
            }
            return value.stripScripts().stripEvents();
          };
          const replaceValue = queryValues[replaceText] ? sanitizer(queryValues[replaceText]) : calculation;
          // eslint-disable-next-line max-len
          const progressText = replaceAll(this.cards[c.index].label, `{${replaceText}}`, `<span class="progressReplace" data-replace-text="${replaceText}">${typeof replaceValue === 'string' ? replaceValue : ''}</span>`);
          cell.querySelector('.jfProgress-itemLabel').innerHTML = `${index + 1}. ${sanitizer(progressText)}`;
        });
      } catch (e) {
        // noop.
      }
    });
  }

  shouldProgressDisplayed() {
    if (this.layoutParams.deviceType === 'mobile' || this.layoutParams.isFeedbackEmbedButton) { return true; }
    const visibleMinCard = 4;
    const visibleItemCount = this.layoutParams.items.filter((p, index) => !this.layoutParams.hiddenIndexes[index]).length;

    return visibleItemCount >= visibleMinCard;
  }

  showProgressBarIfNeeded() {
    if (this.shouldProgressDisplayed()) {
      this.showProgressBar();
    } else {
      this.hideProgressBar();
    }
  }
}
